import type { Ga4TrafficMetrics, Ga4TrafficReport, Ga4TrafficRow } from '@/api/types'
import { GA4_GROUP_BY_LABELS } from '@/api/types'
import { changeTone, DataTable, formatChange, percentChange, type Column } from '@/design'

import { formatMetric, METRICS, type MetricDef } from './metrics'

/** A figure and, when the report was compared, its change against the previous period. */
export function MetricValue({
  metric,
  value,
  previous,
  currency,
}: {
  metric: MetricDef['key']
  value: number | null
  previous: Ga4TrafficMetrics | null
  currency: string | null
}) {
  const change = previous && value !== null ? percentChange(value, previous[metric]) : null
  return (
    <span className="tr-metric">
      <span>{formatMetric(metric, value, currency)}</span>
      {previous ? (
        <small className="tr-delta" data-tone={changeTone(change, 'up_good')} title="Compared with the previous period">
          {change === null ? '—' : formatChange(change)}
        </small>
      ) : null}
    </span>
  )
}

function columnsFor(report: Ga4TrafficReport): Column<Ga4TrafficRow>[] {
  return [
    {
      key: 'label',
      header: GA4_GROUP_BY_LABELS[report.groupBy],
      sortValue: (row) => row.label,
      cell: (row) => row.label,
    },
    ...METRICS.map(
      (metric): Column<Ga4TrafficRow> => ({
        key: metric.key,
        header: metric.header,
        align: 'end',
        sortValue: (row) => row[metric.key] ?? 0,
        cell: (row) => (
          <MetricValue metric={metric.key} value={row[metric.key]} previous={row.previous} currency={report.currency} />
        ),
      }),
    ),
  ]
}

/** One row per group, biggest first; the totals sit above it in `TrafficTotals`. */
export function TrafficTable({ report }: { report: Ga4TrafficReport }) {
  return (
    <>
      <DataTable
        caption={`Traffic by ${GA4_GROUP_BY_LABELS[report.groupBy].toLowerCase()}`}
        columns={columnsFor(report)}
        rows={report.rows}
        rowKey={(row) => row.key}
        initialSort={{ key: 'sessions', direction: 'desc' }}
      />
      <p className="tr-note">
        Users are GA4 users summed over each day, so a returning visitor counts once per day they visit.
        {report.currency === null ? ' These properties report in different currencies, so revenue is not summed: choose one property to see it.' : ''}
      </p>
    </>
  )
}

/** The whole report's figures, with the same deltas as the rows. */
export function TrafficTotals({ report }: { report: Ga4TrafficReport }) {
  return (
    <dl className="tr-totals" aria-label="Totals">
      {METRICS.map((metric) => (
        <div key={metric.key}>
          <dt>{metric.header}</dt>
          <dd>
            <MetricValue
              metric={metric.key}
              value={report.totals[metric.key]}
              previous={report.totalsPrevious}
              currency={report.currency}
            />
          </dd>
        </div>
      ))}
    </dl>
  )
}
